/**
 * Module Partage — contrat partagé.
 *
 * Les identifiants sont NOMINAUX. Le 11/08, un champ typé `Uuid` au lieu
 * d'`IdMetier` a fait refuser par le front la réponse entière d'une file, et le
 * test de contrat ne pouvait pas le voir : il envoyait une liste vide. Un type
 * nominal transforme cette classe de défaut en erreur de compilation.
 */
import { z } from "zod";
import type { Role } from "./roles";

declare const marque: unique symbol;
type Marque<T, N extends string> = T & { readonly [marque]: N };

export type IdPatient = Marque<string, "IdPatient">;
export type IdLieu = Marque<string, "IdLieu">;
export type IdPraticien = Marque<string, "IdPraticien">;
export type IdCas = Marque<string, "IdCas">;
export type CodeTravail = Marque<string, "CodeTravail">;

export const idPatient = (v: string): IdPatient => v as IdPatient;
export const idLieu = (v: string): IdLieu => v as IdLieu;
export const idPraticien = (v: string): IdPraticien => v as IdPraticien;
export const idCas = (v: string): IdCas => v as IdCas;
export const codeTravail = (v: string): CodeTravail => v as CodeTravail;

/** Spec D14 — l'ordre compte : le premier est le défaut. */
export const NIVEAUX_DIVULGATION = ["contact", "travaux", "complet"] as const;
export type NiveauDivulgation = (typeof NIVEAUX_DIVULGATION)[number];

const Uuid = z.string().uuid();
const NomNonVide = z.string().trim().min(1);

/* Les schemas valident ce qui ARRIVE DU RESEAU, ou tout est string. */

export const LieuSchema = z.object({
  id: Uuid,
  praticienId: Uuid,
  /** null = exercice privé, sans cabinet rattaché. */
  cabinetId: Uuid.nullable(),
  libelle: NomNonVide,
});

export const PatientSchema = z.object({
  id: Uuid,
  praticienId: Uuid,
  lieuId: Uuid,
  nom: NomNonVide,
  /** Ce que le praticien déclare à la création : le nom du confrère (spec D12). */
  adresseParLibelle: z.string().trim().min(1).nullable(),
});

/*
 * Les formes de DOMAINE portent des identifiants nominaux. Elles ne derivent
 * pas des schemas par z.infer : un `z.infer` rendrait des `string` nus et les
 * types nominaux ne protegeraient plus rien. La frontiere entre les deux est
 * la route, seul endroit ou l'on convertit.
 */

export interface Lieu {
  id: IdLieu;
  /**
   * `null` = lieu d'un CABINET dont aucun dentiste n'est connu (20/08).
   *
   * ⚠ Vevidental ne renseigne le dentiste que pour 29 % des travaux : 551
   * cabinets actifs n'en ont aucun. Un praticien nul est donc un cas RÉEL et
   * fréquent, pas une impossibilité — ne pas le contourner par `!` ou `as`.
   * La base garantit qu'au moins l'un des deux est présent
   * (`ck_partage_lieu_a_un_proprietaire`, migration 0037).
   */
  praticienId: IdPraticien | null;
  cabinetId: string | null;
  libelle: string;
}

export interface Patient {
  id: IdPatient;
  /** `null` = fiche d'un CABINET dont aucun dentiste n'est connu (20/08). */
  praticienId: IdPraticien | null;
  /** Le propriétaire quand aucun dentiste n'est connu. Cloisonne la fiche. */
  cabinetId: string | null;
  lieuId: IdLieu;
  nom: string;
  adresseParLibelle: string | null;
}

/* -------------------------------------------------------------------- droits
 *
 * Les predicats vivent ICI, pas dans le fichier de routes. §17.5 : jamais deux
 * implementations jumelles serveur/front. Le front qui masquera le bouton et la
 * route qui refuse l'appel appellent LA MEME fonction — sinon l'un des deux
 * derive, et c'est toujours celui qui refuse.
 */

/**
 * Lecture du module (spec D8). Pas `comptabilite` : ce module ne porte rien de
 * financier. Pas `technicien` ni `lecture` : la patientele d'un praticien n'est
 * ni un contexte technique ni un objet d'audit.
 */
export const ROLES_PARTAGE: readonly Role[] = [
  "admin",
  "secretariat",
  "commercial",
  "production",
  "logistique",
];

/**
 * « Sans réponse depuis N jours » — LE seuil, en jours CALENDAIRES, partagé
 * par le bandeau de l'écran ET le rappel du courriel (council v1.90.0, §17.5 :
 * le chiffre du courriel ouvre la page — deux règles donneraient deux nombres).
 */
export const SEUIL_SANS_REPONSE_JOURS = 5;

export function canVoirPartage(roles: readonly Role[]): boolean {
  return roles.some((r) => ROLES_PARTAGE.includes(r));
}

/**
 * Administrer les COMPTES praticiens — etape E9.
 *
 * Deliberement plus etroit que `canVoirPartage`, et c'est tout l'interet :
 * ouvrir un compte donne acces a des dossiers de sante, alors que lire le
 * module ne fait que consulter les siens. Elodie traite les dossiers ; elle
 * n'ouvre pas de comptes. C'est le meme ecart que D13/D14 fait deja entre lire
 * une divulgation et l'ouvrir.
 *
 * `admin` SEUL, donc — et un predicat plutot qu'un `roles.includes("admin")`
 * recopie de part et d'autre (§17.5) : le serveur refuse, l'ecran cache, et les
 * deux disent la meme chose parce qu'ils lisent la meme fonction.
 */
export function canAdministrerComptes(roles: readonly Role[]): boolean {
  return roles.includes("admin");
}

/**
 * Ouverture d'un niveau de divulgation entre deux confreres (spec D14).
 * Restreint a `admin` et `logistique` — choix explicite de Dove. C'est le seul
 * acte du module qui fait franchir des donnees de sante d'un praticien a un
 * autre : il est deliberement plus etroit que la lecture.
 */
export const ROLES_DIVULGATION: readonly Role[] = ["admin", "logistique"];

export function canOuvrirDivulgation(roles: readonly Role[]): boolean {
  return roles.some((r) => ROLES_DIVULGATION.includes(r));
}
